import React, { useState, useEffect } from 'react';
import { Box, Chip, Tooltip, Typography, CircularProgress, useTheme } from '@mui/material';
import TokenIcon from '@mui/icons-material/Token';
import { getTokenBalance } from '../services/tokenService';
import { useAuth } from '../contexts/AuthContext';

/**
 * Component to display the user's token balance
 */
const TokenDisplay = ({ refreshTrigger }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBalance = async () => {
      // Only fetch balance if user is logged in
      if (!user) return;
      
      try {
        setLoading(true);
        const tokenBalance = await getTokenBalance();
        setBalance(tokenBalance);
        setError(null);
      } catch (err) {
        console.error('Error fetching token balance:', err);
        setError('Could not load token balance');
      } finally {
        setLoading(false);
      }
    };

    fetchBalance();
  }, [refreshTrigger, user]);

  // Don't render anything if user is not logged in
  if (!user) return null;

  if (loading) {
    return (
      <Box display="flex" alignItems="center">
        <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
        <Typography variant="body2" sx={{ color: 'white' }}>Loading tokens...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Tooltip title={error}>
        <Chip
          icon={<TokenIcon />}
          label="?"
          size="small"
          sx={{
            bgcolor: '#5E17EB',
            background: '#5E17EB',
            color: 'white',
            fontWeight: 600,
            border: 'none',
            boxShadow: '0 4px 12px rgba(94, 23, 235, 0.25)',
            borderRadius: '16px',
            '& .MuiChip-icon': { color: 'white' }
          }}
        />
      </Tooltip>
    );
  }

  // Determine if we need to show a warning color (for 0 tokens)
  const isZeroTokens = balance === 0;
  
  const tooltipText = balance > 0 
    ? `You have ${balance} token${balance !== 1 ? 's' : ''} available.` 
    : 'You have no tokens available. Please purchase more tokens to continue.';

  return (
    <Tooltip title={tooltipText}>
      <Chip
        icon={<TokenIcon />}
        label={`${balance} Token${balance !== 1 ? 's' : ''} Available`}
        size="small"
        className="token-display-chip"
        sx={{ 
          bgcolor: isZeroTokens ? '#d32f2f !important' : '#5E17EB !important',
          background: isZeroTokens ? '#d32f2f !important' : '#5E17EB !important',
          color: 'white !important',
          fontWeight: 600,
          borderRadius: '16px',
          border: 'none',
          boxShadow: isZeroTokens 
            ? '0 4px 12px rgba(211, 47, 47, 0.25)'
            : '0 4px 12px rgba(94, 23, 235, 0.25)',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: isZeroTokens 
              ? '0 8px 30px rgba(211, 47, 47, 0.35)'
              : '0 8px 30px rgba(94, 23, 235, 0.35)',
            filter: 'brightness(1.05)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 50%, rgba(255, 255, 255, 0) 100%)',
            zIndex: 0,
            pointerEvents: 'none',
          },
          '& .MuiChip-icon': { 
            color: 'white !important' 
          },
          '& .MuiChip-label': {
            color: 'white !important'
          }
        }}
      />
    </Tooltip>
  );
};

export default TokenDisplay; 